"use client";

import React, { useState, useEffect, useRef } from "react";
import { LayoutTemplate, Rocket, Smartphone, SearchCheck, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const services = [{
  id: "01",
  title: "Plataformas Digitales de Alto Rendimiento",
  description: "Desarrollamos sistemas web empresariales con arquitectura escalable, optimizados para conversión, integración con sistemas existentes y máximo ROI.",
  icon: LayoutTemplate
}, {
  id: "02",
  title: "Visibilidad Digital Estratégica",
  description: "Posicionamiento integral mediante SEO técnico, SEM avanzado y optimización multicanal para máxima exposición a clientes potenciales calificados.",
  icon: SearchCheck
}, {
  id: "03",
  title: "Gestión Reputacional & Credibilidad",
  description: "Sistemas automatizados de reputación digital, análisis de percepción de marca y construcción de autoridad en línea mediante testimonios verificados.",
  icon: Rocket
}, {
  id: "04",
  title: "Sistemas de Comunicación Inteligentes",
  description: "Automatización de canales de comunicación con chatbots AI, respuestas inteligentes y gestión centralizada de todas las interacciones comerciales.",
  icon: Smartphone
}];
export default function NuestraIngenieriaWeb() {
  const [activeStep, setActiveStep] = useState(0);
  const observerRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "-40% 0px -40% 0px",
      threshold: 0
    };
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute("data-index"));
          setActiveStep(index);
        }
      });
    }, options);
    observerRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  return <section data-section-id="58902" id="nuestra-estrategia-local" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-[-10%] w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[120px]" />
        <div className="absolute bottom-1/4 right-[-10%] w-[500px] h-[500px] bg-secondary/10 rounded-lg blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          
          {}
          <div className="lg:col-span-5 lg:h-[calc(100vh-8rem)] lg:sticky lg:top-32 flex flex-col justify-center mb-12 lg:mb-0">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs font-medium tracking-wider uppercase">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-lg h-2 w-2 bg-primary"></span>
                </span>
                Transformación Digital
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.1]">
                Arquitectura <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  Tecnológica
                </span>
              </h2>
              
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Soluciones digitales inteligentes que automatizan procesos, integran IA y generan valor sostenible para empresas modernas y escalables.
              </p>

              <div className="pt-8">
                <Button asChild size="lg" className="rounded-sm text-base px-8 shadow-lg shadow-primary/20 bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Link to="#contacto">
                    Solicitar Diagnóstico <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 flex flex-col gap-32 pb-32">
            {services.map((service, index) => {
            const isActive = activeStep === index;
            return <div key={service.id} data-index={index} ref={el => {
              if (el) observerRefs.current[index] = el;
            }} className={cn("group relative transition-all duration-700 ease-out flex flex-col md:flex-row gap-8 items-start md:items-center p-8 rounded-sm border border-transparent", isActive ? "opacity-100 bg-card/40 border-primary/20 shadow-2xl shadow-primary/10 backdrop-blur-md translate-x-0" : "opacity-30 hover:opacity-60 translate-x-4 md:translate-x-8 grayscale hover:grayscale-0")}>
                  {}
                  <div className={cn("text-7xl md:text-8xl font-black tracking-tighter leading-none transition-colors duration-500 select-none", isActive ? "text-transparent [-webkit-text-stroke:2px_hsl(var(--primary))]" : "text-transparent [-webkit-text-stroke:2px_hsl(var(--muted-foreground))]")} aria-hidden="true">
                    {service.id}
                  </div>

                  <div className="flex-1 space-y-4">
                    <div className="flex items-center gap-4 mb-2">
                      <div className={cn("p-3 rounded-sm transition-all duration-500 group-hover:scale-110", isActive ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground")}>
                        <service.icon className="w-8 h-8 stroke-[1.5]" />
                      </div>
                      <h3 className={cn("text-2xl md:text-3xl font-bold tracking-tight transition-colors duration-500", isActive ? "text-foreground" : "text-muted-foreground")}>
                        {service.title}
                      </h3>
                    </div>
                    
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className={cn("h-1 w-0 bg-gradient-to-r from-primary to-secondary transition-all duration-1000 ease-out mt-6", isActive && "w-full")} />
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}